import { FC, useId } from "react";

import { Stack } from "@mui/material";

import { useLocaleField } from "../../../../../helpers/useLocaleField";

import { useTranslate } from "@/common/hooks/useTranslate";
import { RateCategoryOutput } from "@/common/types/trpc";
import { FormButton } from "@/forms-design-system/FormButton";
import { FormTypography } from "@/forms-design-system/FormTypography";

export const TICKETING_SELECTION_V2_CATEGORIES_ID = "ticketing-selection-v2-categories";

interface SortByCategoryPillsProps {
  categories: RateCategoryOutput[];
  selectedCategoryId: string | null;
  onSelect: (categoryId: string | null) => void;
}

export const SortByCategoryPills: FC<SortByCategoryPillsProps> = ({ categories, selectedCategoryId, onSelect }) => {
  const { t } = useTranslate();
  const getLocaleField = useLocaleField();
  const labelId = useId();

  if (categories.length === 0) {
    return null;
  }

  return (
    <Stack id={TICKETING_SELECTION_V2_CATEGORIES_ID} direction="column" gap={1} data-test="sort-by-category-pills">
      <FormTypography id={labelId} variant="body2" vibe="quiet">
        {t("ticketing", "form.sortByCategory")}
      </FormTypography>
      <Stack
        role="group"
        aria-labelledby={labelId}
        direction="row"
        gap={1}
        sx={{
          flexWrap: { xs: "nowrap", md: "wrap" },
          overflowX: { xs: "auto", md: "visible" },
          scrollbarWidth: "none",
          "::-webkit-scrollbar": { display: "none" },
        }}
      >
        {categories.map((category) => {
          const localeField = getLocaleField(category.fields);
          if (!localeField) {
            return null;
          }
          const isSelected = selectedCategoryId === category.id;
          return (
            <FormButton
              key={category.id}
              variant="rounded"
              size="small"
              isSelected={isSelected}
              onClick={() => onSelect(isSelected ? null : category.id)}
              data-test={`sort-by-category-pill-${category.id}`}
              sx={{ flexShrink: 0 }}
            >
              {localeField.name}
            </FormButton>
          );
        })}
      </Stack>
    </Stack>
  );
};
